import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiStream } from "@/lib/api";

import { type PanelVizSpec } from "./viz";

// ── Types (mirror backend schemas.py) ──────────────────────────────────────

export type ReportStatus = "active" | "archived";
export type ShareRole = "viewer" | "editor";
export type PanelType = "chart" | "grid" | "stat" | "text";
export type QuerySource = "snowflake" | "postgres";

export interface PanelLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PanelQuery {
  source: QuerySource;
  sql: string;
}

export interface ReportPanel {
  id: string;
  type: PanelType;
  title?: string | null;
  description?: string | null;
  layout: PanelLayout;
  query?: PanelQuery | null;
  viz: PanelVizSpec;
}

export type FilterType = "text" | "date" | "select";

export interface FilterDef {
  param: string;
  label?: string | null;
  type: FilterType;
  default?: string | null;
  options?: string[] | null;
  /** Client-side auto-filter: the lowercase result column to match rows on. When
   *  set, the viewer filters every panel whose result has this column by
   *  `row[column] == value` — no `{{param}}` needed in the panel SQL. */
  column?: string | null;
}

export interface ReportConfig {
  version: number;
  panels: ReportPanel[];
  filters?: FilterDef[];
}

export interface ReportShareRead {
  user_id: string;
  role: ShareRole;
  user_name?: string | null;
  user_email?: string | null;
}

export interface ReportSummary {
  id: string;
  title: string;
  description?: string | null;
  status: ReportStatus;
  created_by?: string | null;
  created_by_name?: string | null;
  panel_count: number;
  shared: boolean;
  can_edit: boolean;
  can_manage: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReportDetail {
  id: string;
  title: string;
  description?: string | null;
  status: ReportStatus;
  config: ReportConfig;
  created_by?: string | null;
  created_by_name?: string | null;
  can_edit: boolean;
  can_manage: boolean;
  shares: ReportShareRead[];
  created_at: string;
  updated_at: string;
}

export interface ShareableUser {
  id: string;
  name?: string | null;
  email: string;
}

export interface ReportCreateInput {
  title: string;
  description?: string | null;
  config?: ReportConfig;
}

export interface ReportUpdateInput {
  title?: string;
  description?: string | null;
  config?: ReportConfig;
}

export interface ShareInput {
  user_id: string;
  role: ShareRole;
}

// ── Queries ─────────────────────────────────────────────────────────────────

export function useReports(includeArchived = false) {
  return useQuery({
    queryKey: ["reports", "list", includeArchived],
    queryFn: () =>
      apiFetch<ReportSummary[]>(`/reports?include_archived=${includeArchived}`),
    staleTime: 30_000,
  });
}

export function useReport(id: string | undefined) {
  return useQuery({
    queryKey: ["reports", "detail", id],
    queryFn: () => apiFetch<ReportDetail>(`/reports/${id}`),
    enabled: !!id,
  });
}

export function useShareableUsers(enabled = true) {
  return useQuery({
    queryKey: ["reports", "users"],
    queryFn: () => apiFetch<ShareableUser[]>(`/reports/users`),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useReportShares(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["reports", "shares", id],
    queryFn: () => apiFetch<ReportShareRead[]>(`/reports/${id}/shares`),
    enabled: !!id && enabled,
  });
}

export interface PreviewQueryResponse {
  source: QuerySource;
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  truncated: boolean;
  masked_columns: string[];
}

/** Run a saved panel's read-only query and cache the rows — the builder's saved
 *  panel cards + the dashboard viewer render from this. `params` fills the
 *  query's {{name}} placeholders (dashboard filters); changing them refetches. */
export function usePreviewQuery(
  source: QuerySource | undefined,
  sql: string | undefined,
  params?: Record<string, string>,
  enabled = true,
) {
  return useQuery({
    queryKey: ["reports", "preview-query", source, sql, params ?? {}],
    queryFn: () =>
      apiFetch<PreviewQueryResponse>(`/reports/preview-query`, {
        method: "POST",
        body: { source, sql, params: params ?? {} },
      }),
    enabled: enabled && !!source && !!sql,
    staleTime: 60_000,
  });
}

export interface PanelDataResponse extends PreviewQueryResponse {
  cached: boolean;
  cached_at: string | null;
  stale: boolean;
}

/** A saved panel's rows for the VIEWER, served from the server-side cache
 *  (TTL-gated) so Snowflake panels don't re-query on every dashboard load. The
 *  query is read from the saved config server-side; `params` fills its {{name}}
 *  filters. (Authoring uses {@link usePreviewQuery}, which always runs live.) */
export function usePanelData(
  reportId: string | undefined,
  panelId: string | undefined,
  params?: Record<string, string>,
  enabled = true,
) {
  return useQuery({
    queryKey: ["reports", "panel-data", reportId, panelId, params ?? {}],
    queryFn: () =>
      apiFetch<PanelDataResponse>(`/reports/${reportId}/panels/${panelId}/data`, {
        method: "POST",
        body: { params: params ?? {} },
      }),
    enabled: enabled && !!reportId && !!panelId,
    staleTime: 60_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReportCreateInput) =>
      apiFetch<ReportDetail>(`/reports`, { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reports", "list"] }),
  });
}

export function useUpdateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReportUpdateInput }) =>
      apiFetch<ReportDetail>(`/reports/${id}`, { method: "PATCH", body: input }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["reports", "list"] });
      qc.setQueryData(["reports", "detail", data.id], data);
    },
  });
}

export function useArchiveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiFetch<ReportDetail>(`/reports/${id}/archive`, {
        method: "POST",
        body: { archived },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reports", "list"] }),
  });
}

/** Drop the report's server-side panel cache, then refetch the viewer's panel
 *  data (which re-runs the queries live and re-caches). */
export function useRefreshReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ invalidated: number }>(`/reports/${id}/refresh`, { method: "POST" }),
    onSuccess: (_data, id) =>
      qc.invalidateQueries({ queryKey: ["reports", "panel-data", id] }),
  });
}

export function useSetShares() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shares }: { id: string; shares: ShareInput[] }) =>
      apiFetch<ReportShareRead[]>(`/reports/${id}/shares`, {
        method: "PUT",
        body: { shares },
      }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ["reports", "shares", id] });
      void qc.invalidateQueries({ queryKey: ["reports", "list"] });
    },
  });
}

// ── AI: model picker + agent conversation ───────────────────────────────────

export type AiProvider = "ollama" | "anthropic" | "openai";

export interface AiModel {
  id: string;
  provider: AiProvider;
  name: string;
  is_default: boolean;
}

export interface AiModelsResponse {
  models: AiModel[];
  default_id: string | null;
}

export type MessageRole = "user" | "assistant" | "tool";

export interface ToolCall {
  id: string;
  function: { name: string; arguments: Record<string, unknown> };
}

/** A panel the agent recommends (propose_panel tool) — rendered in the chat as
 *  a previewable "Add to report" card. */
export interface PanelProposal {
  title: string | null;
  type: PanelType;
  query: PanelQuery;
  viz: PanelVizSpec;
}

export interface ToolResultMeta {
  sql?: string;
  source?: QuerySource;
  ok?: boolean;
  error?: string;
  row_count?: number;
  truncated?: boolean;
  columns?: string[];
  rows?: Record<string, unknown>[];
  masked_columns?: string[];
  model?: string;
  /** Present on a propose_panel tool row — the recommended panel. */
  proposal?: PanelProposal;
}

export interface AgentMessage {
  id: string;
  seq: number;
  role: MessageRole;
  content?: string | null;
  tool_calls: ToolCall[];
  tool_result_meta?: ToolResultMeta | null;
  created_at: string;
}

export function useAiModels(enabled = true) {
  return useQuery({
    queryKey: ["reports", "ai-models"],
    queryFn: () => apiFetch<AiModelsResponse>(`/reports/ai/models`),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useReportMessages(id: string | undefined) {
  return useQuery({
    queryKey: ["reports", "messages", id],
    queryFn: () => apiFetch<AgentMessage[]>(`/reports/${id}/messages`),
    enabled: !!id,
  });
}

export function useAgentChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content, modelId }: { id: string; content: string; modelId: string | null }) =>
      apiFetch<{ messages: AgentMessage[] }>(`/reports/${id}/agent/chat`, {
        method: "POST",
        body: { content, model_id: modelId },
      }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ["reports", "messages", id] });
    },
  });
}

/**
 * Stream an agent turn over SSE, invoking `onMessage` for each message the agent
 * persists (user → assistant → tool …) so the thread fills in live. Resolves
 * when the server sends `{type:"done"}`; throws on `{type:"error"}` or transport
 * failure. Not a hook — call it from an event handler and update the messages
 * cache in `onMessage`.
 */
export async function streamAgentChat(
  id: string,
  content: string,
  modelId: string | null,
  onMessage: (message: AgentMessage) => void,
): Promise<void> {
  const resp = await apiStream(`/reports/${id}/agent/chat/stream`, {
    method: "POST",
    body: { content, model_id: modelId },
  });
  const reader = resp.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      const raw = dataLine.slice(5).trim();
      if (!raw) continue;
      let evt: unknown;
      try {
        evt = JSON.parse(raw);
      } catch {
        continue;
      }
      if (evt && typeof evt === "object" && "type" in evt) {
        const t = (evt as { type?: string; detail?: string }).type;
        if (t === "error") throw new Error((evt as { detail?: string }).detail ?? "stream error");
        if (t === "done") return;
      } else {
        onMessage(evt as AgentMessage);
      }
    }
  }
}
